import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileSpreadsheet,
  FileText,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Loader2,
  Trash2,
  Eye,
  ChevronDown,
  ChevronUp,
  Bot,
  Zap,
  Clock,
  Image as ImageIcon,
  AlertTriangle,
  DollarSign,
  X,
  ZoomIn,
  Globe,
  Instagram,
  SkipForward,
  Play,
  List
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function MassUploadTab({ businessId }) {
  // Paso 1: Excel
  const [priceListFile, setPriceListFile] = useState(null);
  const [priceListData, setPriceListData] = useState(null);
  const [loadingPrices, setLoadingPrices] = useState(false);

  // Paso 2: PDFs
  const [specSheetFiles, setSpecSheetFiles] = useState([]);
  const specInputRef = useRef(null);

  // Paso 3: Cola de ficheros pendientes (solo extracción)
  const [fileQueue, setFileQueue] = useState([]); // {fileName, file, status: pending|done|error}
  const [versionQueue, setVersionQueue] = useState([]); // Versiones extraídas, pendientes de procesar
  const [isExtracting, setIsExtracting] = useState(false);

  // Paso 4: Procesamiento UNO a UNO
  const [currentVehicle, setCurrentVehicle] = useState(null); // El vehículo actualmente en proceso/revisión
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState("");

  // Historial de completados
  const [completedVehicles, setCompletedVehicles] = useState([]);

  // Saving
  const [isSaving, setIsSaving] = useState(false);

  // Lightbox
  const [lightboxImg, setLightboxImg] = useState(null);

  // ========================
  // PASO 1: Subir Excel
  // ========================
  const handlePriceListUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast.error("Solo se aceptan archivos Excel (.xlsx o .xls)");
      return;
    }
    setPriceListFile(file);
    setLoadingPrices(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("business_id", businessId);
      const res = await axios.post(`${API}/api/mass-upload/parse-price-list`, formData);
      if (res.data.success && res.data.vehicles?.length > 0) {
        setPriceListData(res.data.vehicles);
        toast.success(`${res.data.vehicles.length} vehículos encontrados`);
      } else {
        toast.error(res.data.error || "No se encontraron vehículos");
      }
    } catch (err) {
      toast.error("Error al procesar la lista");
    } finally {
      setLoadingPrices(false);
    }
  };

  // ========================
  // PASO 2: Subir fichas
  // ========================
  const handleSpecSheetsUpload = (e) => {
    const files = Array.from(e.target.files);
    const valid = files.filter((f) => /\.(pdf|png|jpg|jpeg)$/i.test(f.name));
    if (valid.length !== files.length) toast.warning("Solo PDF, PNG, JPG.");
    setSpecSheetFiles((prev) => [...prev, ...valid]);
    toast.success(`${valid.length} fichas agregadas`);
    if (specInputRef.current) specInputRef.current.value = "";
  };

  const removeSpecSheet = (i) => setSpecSheetFiles((prev) => prev.filter((_, idx) => idx !== i));

  // ========================
  // MATCHING: precio por versión
  // ========================
  const findPriceForVersion = (brand, modelName, versionName) => {
    if (!priceListData) return null;
    const normModel = (modelName || "").toUpperCase().replace(/-/g, "");
    const normVersion = (versionName || "").toUpperCase().trim();

    // Exact model + version word overlap
    for (const p of priceListData) {
      const pm = (p.model || "").toUpperCase().replace(/-/g, "");
      const pv = (p.version || "").toUpperCase().trim();
      if (pm === normModel && normVersion && pv) {
        const vWords = normVersion.split(/\s+/).filter((w) => w.length > 2);
        const pvWords = pv.split(/\s+/).filter((w) => w.length > 2);
        const overlap = vWords.filter((w) => pvWords.some((pw) => pw.includes(w) || w.includes(pw)));
        if (overlap.length >= 1) return p;
      }
    }
    // Model only
    for (const p of priceListData) {
      const pm = (p.model || "").toUpperCase().replace(/-/g, "");
      if (pm === normModel) return p;
    }
    return null;
  };

  // ========================
  // PASO 3: Extraer datos de TODOS los PDFs primero
  // ========================
  const startExtraction = async () => {
    if (specSheetFiles.length === 0) {
      toast.error("Sube al menos una ficha técnica");
      return;
    }
    setIsExtracting(true);
    setVersionQueue([]);
    setCurrentVehicle(null);
    setCompletedVehicles([]);

    const fq = specSheetFiles.map((f, i) => ({ id: i, fileName: f.name, file: f, status: "pending" }));
    setFileQueue([...fq]);

    const allVersions = [];
    let vIdx = 0;

    for (let fi = 0; fi < fq.length; fi++) {
      fq[fi].status = "extracting";
      setFileQueue([...fq]);

      try {
        const formData = new FormData();
        formData.append("file", fq[fi].file);
        formData.append("business_id", businessId);
        const res = await axios.post(`${API}/api/mass-upload/process-spec-sheet`, formData, { timeout: 120000 });

        if (!res.data.success) {
          fq[fi].status = "error";
          fq[fi].error = res.data.error;
          setFileQueue([...fq]);
          continue;
        }

        const extracted = res.data.data;
        const vehicleImage = res.data.vehicle_image;
        const specSheetUrl = res.data.spec_sheet_url;

        const versions = extracted.versions ? Object.entries(extracted.versions) : [["UNICA", { name: extracted.model_name || "Única", specifications: [] }]];

        for (const [vKey, vData] of versions) {
          const versionName = vData.name || vKey;
          const matched = findPriceForVersion(extracted.brand, extracted.model_name, versionName);

          allVersions.push({
            id: vIdx++,
            fileName: fq[fi].fileName,
            brand: extracted.brand || "",
            modelName: extracted.model_name || "",
            year: extracted.year || matched?.year || "",
            versionKey: vKey,
            versionName: versionName,
            specifications: vData.specifications || [],
            vehicleImage: vehicleImage,
            specSheetUrl: specSheetUrl,
            matchedPrice: matched,
            editedPrice: matched ? String(matched.price_list || "") : "",
            editedPurchase: matched ? String(matched.price_aaa || "") : "",
            webImages: [],
            socialImages: [],
            copyFeed: "",
            copyStories: "",
            hashtagsFeed: [],
            hashtagsStories: [],
            status: "pending_generation", // pending_generation, generating, review, completed, discarded
          });
        }

        fq[fi].status = "done";
        setFileQueue([...fq]);
        setVersionQueue([...allVersions]);
      } catch (err) {
        fq[fi].status = "error";
        fq[fi].error = err.message;
        setFileQueue([...fq]);
      }
    }

    setIsExtracting(false);

    if (allVersions.length > 0) {
      toast.success(`${allVersions.length} versiones extraídas. Procesemos una por una.`);
    } else {
      toast.error("No se extrajeron versiones de ningún archivo");
    }
  };

  // ========================
  // PASO 4: Abrir el siguiente vehículo pendiente para revisión
  // (NO genera imágenes automáticamente — espera confirmación manual)
  // ========================
  const processNextVehicle = () => {
    const next = versionQueue.find((v) => v.status === "pending_generation");
    if (!next) {
      toast.info("No hay más vehículos pendientes");
      return;
    }
    // Solo abrir panel de revisión. El usuario debe pulsar
    // "Iniciar Generación de Imágenes" para arrancar la generación.
    setCurrentVehicle({ ...next });
  };

  const processVehicle = async (vehicleId) => {
    const vehicle = versionQueue.find((v) => v.id === vehicleId);
    if (!vehicle) return;

    setIsGenerating(true);
    vehicle.status = "generating";
    setCurrentVehicle({ ...vehicle });
    setVersionQueue([...versionQueue]);

    try {
      // 1. Generate prompts + copy
      setGenerationStep("Generando textos publicitarios...");
      const priceForPrompt = vehicle.editedPrice || "0";
      let promptData = null;
      try {
        const promptRes = await axios.post(`${API}/api/generate-prompt`, {
          model_name: `${vehicle.brand} ${vehicle.modelName}`,
          version: vehicle.versionName,
          year: vehicle.year,
          price: priceForPrompt,
          specifications: vehicle.specifications.slice(0, 9),
          business_id: businessId,
        }, { timeout: 60000 });
        if (promptRes.data.success) {
          promptData = promptRes.data;
          vehicle.copyFeed = promptData.copy_feed || "";
          vehicle.copyStories = promptData.copy_stories || "";
          vehicle.hashtagsFeed = promptData.hashtags_feed || [];
          vehicle.hashtagsStories = promptData.hashtags_stories || [];
        }
      } catch (e) {
        console.error("Error generando prompts:", e);
      }

      // 2. Build image jobs
      const imageJobs = [];

      // 3 web images — escenarios elegidos AL AZAR de un pool amplio para que
      // cada vehículo tenga fondos únicos (evita que "Web 1/2/3" sea siempre
      // showroom + carretera + urbano).
      const webScenePool = [
        "Modern automotive showroom, dramatic studio lighting, glossy reflective floor",
        "Open mountain highway at golden hour, cinematic landscape",
        "Downtown skyline at twilight, city lights bokeh background",
        "Luxury dealership, polished concrete floor, ambient warm lighting",
        "Coastal road with ocean panorama, late afternoon sun",
        "Rainy night city street, neon reflections, wet asphalt",
        "Desert highway at dramatic sunset, wide open landscape",
        "Forest road, misty morning, lush green surroundings",
        "Modern glass-facade parking, industrial concrete, spotlight beams",
        "Vineyard countryside road, golden hour, elegant scenery",
        "Snowy alpine road, dawn light, dramatic mountains",
        "Elegant private estate driveway, marble tiles, sunset backlight",
        "Cinematic canyon overlook, warm cinematic tones",
        "Rooftop helipad at sunset, city skyline in background",
      ];
      // Barajar y tomar 3 escenarios distintos
      const shuffled = [...webScenePool].sort(() => Math.random() - 0.5).slice(0, 3);
      for (let w = 0; w < 3; w++) {
        imageJobs.push({
          category: "web", name: `Web ${w + 1}`,
          prompt: `Professional automotive photography: ${vehicle.brand} ${vehicle.modelName} ${vehicle.versionName} ${vehicle.year}. ${shuffled[w]}. Clean commercial photography, NO text, NO logos, NO watermarks. Photorealistic.`,
          ratio: "16:9",
        });
      }

      // Carousel from promptData
      if (promptData?.carousel) {
        for (const cp of promptData.carousel) {
          imageJobs.push({ category: "carousel", name: cp.name || "Carrusel", prompt: cp.prompt || "", ratio: cp.ratio || "4:5" });
        }
      }
      // Stories
      if (promptData?.prompt_stories) {
        imageJobs.push({ category: "stories", name: "Stories", prompt: promptData.prompt_stories, ratio: "9:16" });
      }

      // 3. Generate each image
      const webImgs = [];
      const socialImgs = [];

      for (let j = 0; j < imageJobs.length; j++) {
        const job = imageJobs[j];
        setGenerationStep(`Generando ${job.name} (${j + 1}/${imageJobs.length})...`);

        try {
          const imgRes = await axios.post(`${API}/api/generate-image`, {
            prompt: job.prompt, aspect_ratio: job.ratio, vehicle_image_base64: vehicle.vehicleImage || null,
          }, { timeout: 90000 });

          const imgObj = {
            category: job.category, name: job.name, type: job.category, ratio: job.ratio,
            url: imgRes.data.success ? imgRes.data.data?.url || "" : "",
            status: imgRes.data.success ? "generated" : "error",
            error: imgRes.data.error || null, prompt: job.prompt,
          };
          if (job.category === "web") webImgs.push(imgObj); else socialImgs.push(imgObj);
        } catch (imgErr) {
          const is502 = imgErr.message?.includes("502") || imgErr.response?.status === 502;
          const imgObj = {
            category: job.category, name: job.name, type: job.category, ratio: job.ratio,
            url: "", status: "error", error: is502 ? "Error temporal 502" : imgErr.message, prompt: job.prompt,
          };
          if (job.category === "web") webImgs.push(imgObj); else socialImgs.push(imgObj);
        }

        if (j < imageJobs.length - 1) await new Promise((r) => setTimeout(r, 2000));
      }

      vehicle.webImages = webImgs;
      vehicle.socialImages = socialImgs;
      vehicle.status = "review";
      setCurrentVehicle({ ...vehicle });
      setVersionQueue([...versionQueue]);
      toast.success(`${vehicle.brand} ${vehicle.modelName} ${vehicle.versionName} listo para revisión`);
    } catch (err) {
      vehicle.status = "pending_generation";
      vehicle.error = err.message;
      setCurrentVehicle({ ...vehicle });
      setVersionQueue([...versionQueue]);
      toast.error("Error: " + err.message);
    } finally {
      setIsGenerating(false);
      setGenerationStep("");
    }
  };

  // ========================
  // REGENERATE IMAGE
  // ========================
  const regenerateImage = async (imgCategory, imgIndex) => {
    if (!currentVehicle) return;
    const imgArray = imgCategory === "web" ? "webImages" : "socialImages";
    const oldImg = currentVehicle[imgArray][imgIndex];
    if (!oldImg) return;

    // Mark regenerating
    const updated = { ...currentVehicle };
    updated[imgArray] = [...updated[imgArray]];
    updated[imgArray][imgIndex] = { ...oldImg, status: "regenerating" };
    setCurrentVehicle(updated);

    try {
      const randomStyles = [
        "Luxury dealership, polished concrete, ambient lighting",
        "Coastal road, ocean view, afternoon light",
        "Night city, wet streets, neon reflections",
        "Desert highway, dramatic sunset, wide landscape",
        "Forest road, misty morning, green environment",
        "Parking garage, industrial concrete, dramatic spotlight",
        "Vineyard road, golden fields, elegant setting",
      ];
      const style = randomStyles[Math.floor(Math.random() * randomStyles.length)];

      const prompt = imgCategory === "web"
        ? `Professional automotive photography: ${currentVehicle.brand} ${currentVehicle.modelName} ${currentVehicle.versionName} ${currentVehicle.year}. ${style}. Clean commercial photography, NO text, NO logos. Photorealistic.`
        : oldImg.prompt || `Professional advertising image of ${currentVehicle.brand} ${currentVehicle.modelName}. High quality.`;

      const imgRes = await axios.post(`${API}/api/generate-image`, {
        prompt, aspect_ratio: oldImg.ratio || (imgCategory === "web" ? "16:9" : "4:5"),
        vehicle_image_base64: currentVehicle.vehicleImage || null,
      }, { timeout: 90000 });

      const refreshed = { ...currentVehicle };
      refreshed[imgArray] = [...refreshed[imgArray]];
      refreshed[imgArray][imgIndex] = {
        ...refreshed[imgArray][imgIndex],
        url: imgRes.data.success ? imgRes.data.data?.url || "" : "",
        status: imgRes.data.success ? "generated" : "error",
        error: imgRes.data.error || null,
      };
      setCurrentVehicle(refreshed);

      // Also update versionQueue
      setVersionQueue((prev) => prev.map((v) => v.id === currentVehicle.id ? refreshed : v));

      if (imgRes.data.success) toast.success(`${oldImg.name} regenerada`);
      else toast.error("Error al regenerar");
    } catch (err) {
      const refreshed = { ...currentVehicle };
      refreshed[imgArray] = [...refreshed[imgArray]];
      refreshed[imgArray][imgIndex] = { ...refreshed[imgArray][imgIndex], status: "error", error: err.message };
      setCurrentVehicle(refreshed);
      toast.error("Error: " + err.message);
    }
  };

  // ========================
  // REGENERATE COPY (Feed/Stories)
  // ========================
  const regenerateCopy = async (which) => {
    // which = "feed" | "stories"
    if (!currentVehicle) return;
    const upd = { ...currentVehicle, copyRegenerating: which };
    setCurrentVehicle(upd);
    setVersionQueue((prev) => prev.map((v) => v.id === currentVehicle.id ? upd : v));
    try {
      const res = await axios.post(`${API}/api/mass-upload/generate-prompts`, {
        brand: currentVehicle.brand, model_name: currentVehicle.modelName,
        version: currentVehicle.versionName, year: currentVehicle.year,
        specifications: currentVehicle.specifications || [], price: currentVehicle.editedPrice || "",
        business_id: businessId,
      });
      if (res.data?.success) {
        const p = res.data.prompts || res.data;
        const updated = { ...upd, copyRegenerating: null };
        if (which === "feed") {
          updated.copyFeed = p.copy_feed || currentVehicle.copyFeed;
          updated.hashtagsFeed = p.hashtags_feed || currentVehicle.hashtagsFeed;
        } else {
          updated.copyStories = p.copy_stories || currentVehicle.copyStories;
          updated.hashtagsStories = p.hashtags_stories || currentVehicle.hashtagsStories;
        }
        setCurrentVehicle(updated);
        setVersionQueue((prev) => prev.map((v) => v.id === currentVehicle.id ? updated : v));
        toast.success(`Copy ${which} regenerado`);
      } else {
        toast.error("Error al regenerar copy");
        const reset = { ...upd, copyRegenerating: null };
        setCurrentVehicle(reset);
        setVersionQueue((prev) => prev.map((v) => v.id === currentVehicle.id ? reset : v));
      }
    } catch (err) {
      toast.error("Error regenerando copy: " + err.message);
      const reset = { ...upd, copyRegenerating: null };
      setCurrentVehicle(reset);
      setVersionQueue((prev) => prev.map((v) => v.id === currentVehicle.id ? reset : v));
    }
  };

  // ========================
  // SAVE / APPROVE
  // ========================
  const saveCurrentVehicle = async () => {
    if (!currentVehicle) return;
    setIsSaving(true);

    try {
      const v = currentVehicle;
      const webImgs = (v.webImages || []).filter((i) => i.status === "generated" && i.url);
      const carouselImgs = (v.socialImages || []).filter((i) => i.category === "carousel" && i.status === "generated" && i.url);
      const storiesImgs = (v.socialImages || []).filter((i) => i.category === "stories" && i.status === "generated" && i.url);

      // 1. Save vehicle
      const vehicleData = {
        business_id: businessId,
        brand: v.brand, model_name: v.modelName, version: v.versionName, year: v.year,
        price: v.editedPrice || "", purchase_price: v.editedPurchase || "",
        spec_sheet_url: v.specSheetUrl || "", specifications: v.specifications || [],
        vehicle_image: v.vehicleImage || "",
        web_image: webImgs.length > 0 ? webImgs[0].url : "",
        web_gallery_images: webImgs.map((i) => ({ name: i.name, url: i.url, type: "web" })),
        gallery_images: carouselImgs.map((i) => ({ name: i.name, url: i.url, type: "carousel" })),
      };

      const vehicleRes = await axios.post(`${API}/api/vehicles/create-single`, vehicleData);
      const savedId = vehicleRes.data?.vehicle?.id || "";

      // 2. Create publication
      if ((carouselImgs.length > 0 || storiesImgs.length > 0) && v.copyFeed) {
        const allImgs = [
          ...webImgs.map((i) => ({ name: i.name, url: i.url, type: "web" })),
          ...carouselImgs.map((i) => ({ name: i.name, url: i.url, type: "carousel" })),
          ...storiesImgs.map((i) => ({ name: i.name, url: i.url, type: "stories" })),
        ];
        try {
          await axios.post(`${API}/api/publications/confirm`, {
            business_id: businessId, vehicle_id: savedId,
            vehicle_name: `${v.modelName} ${v.versionName}`,
            images: allImgs, copy_feed: v.copyFeed, copy_stories: v.copyStories,
            hashtags_feed: v.hashtagsFeed, hashtags_stories: v.hashtagsStories,
          });
        } catch (pubErr) {
          console.error("Error creando publicación:", pubErr);
        }
      }

      // Update status
      v.status = "completed";
      setCurrentVehicle({ ...v });
      setVersionQueue((prev) => prev.map((vq) => vq.id === v.id ? { ...vq, status: "completed" } : vq));
      setCompletedVehicles((prev) => [...prev, { ...v }]);
      toast.success(`${v.brand} ${v.modelName} ${v.versionName} guardado en BD + Publicaciones`);
    } catch (err) {
      toast.error("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setIsSaving(false);
    }
  };

  const discardCurrentVehicle = () => {
    if (!currentVehicle) return;
    currentVehicle.status = "discarded";
    setVersionQueue((prev) => prev.map((v) => v.id === currentVehicle.id ? { ...v, status: "discarded" } : v));
    setCurrentVehicle(null);
    toast.info("Vehículo descartado");
  };

  const goToNextVehicle = () => {
    setCurrentVehicle(null);
    // Abrir panel de revisión del siguiente pendiente SIN arrancar generación.
    // La generación se dispara únicamente con el botón "Iniciar Generación de Imágenes".
    const next = versionQueue.find((v) => v.status === "pending_generation");
    if (next) {
      setCurrentVehicle({ ...next });
    } else {
      toast.success("Todos los vehículos han sido procesados");
    }
  };

  // ========================
  // UI Helpers
  // ========================
  const pendingCount = versionQueue.filter((v) => v.status === "pending_generation").length;
  const completedCount = versionQueue.filter((v) => v.status === "completed").length;
  const totalVersions = versionQueue.length;

  const ImageThumb = ({ img, imgCategory, imgIndex, large }) => (
    <div className={`relative group ${large ? "" : ""}`}>
      {img.status === "generated" && img.url ? (
        <img src={img.url} alt={img.name}
          className={`w-full ${large ? "h-56" : "h-52"} object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity`}
          onClick={() => setLightboxImg(img.url)} />
      ) : img.status === "regenerating" ? (
        <div className={`w-full ${large ? "h-56" : "h-52"} bg-slate-700 rounded-lg flex items-center justify-center`}>
          <Loader2 className="w-5 h-5 text-orange-400 animate-spin" />
        </div>
      ) : (
        <div className={`w-full ${large ? "h-56" : "h-52"} bg-red-500/10 rounded-lg flex flex-col items-center justify-center p-2 border border-red-500/40`}>
          <AlertTriangle className="w-6 h-6 text-amber-400 mb-1" />
          <span className="text-xs text-red-300 text-center font-medium">
            {img.error?.includes("502") || img.error?.includes("503") || img.error?.includes("520")
              ? "Error temporal del servidor de IA"
              : img.error?.includes("timeout") ? "Tiempo de espera agotado"
              : img.error?.includes("safety") || img.error?.includes("policy") ? "Bloqueado por política de contenido"
              : img.error?.includes("quota") || img.error?.includes("rate") ? "Cuota o rate limit excedido"
              : "Error generando imagen"}
          </span>
          <span className="text-[10px] text-slate-400 text-center mt-1">Toca 🔄 para reintentar</span>
        </div>
      )}
      {/* Botón regenerar SIEMPRE visible (antes solo aparecía en hover, ahora prominente) */}
      <button onClick={(e) => { e.stopPropagation(); regenerateImage(imgCategory, imgIndex); }}
        disabled={img.status === "regenerating"}
        className="absolute bottom-2 right-2 flex items-center gap-1 px-2.5 py-1.5 bg-slate-900/90 rounded-lg text-orange-400 hover:bg-slate-800 hover:text-orange-300 text-xs font-medium shadow-lg disabled:opacity-50"
        data-testid={`regen-${imgCategory}-${imgIndex}`}>
        <RefreshCw className={`w-3.5 h-3.5 ${img.status === "regenerating" ? "animate-spin" : ""}`} />
        Regenerar
      </button>
      {img.status === "generated" && img.url && (
        <button onClick={(e) => { e.stopPropagation(); setLightboxImg(img.url); }}
          className="absolute top-2 right-2 p-1.5 bg-slate-900/90 rounded-lg text-white hover:bg-slate-800 shadow-lg"
          title="Ver en grande">
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
      )}
      <span className="absolute top-2 left-2 text-xs bg-slate-900/90 px-2 py-1 rounded text-slate-200 font-semibold shadow-lg">{img.name}</span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="mass-upload-tab">
      {/* Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightboxImg(null)} data-testid="lightbox-modal">
          <button onClick={() => setLightboxImg(null)} className="absolute top-4 right-4 p-2 bg-slate-800 rounded-full text-white hover:bg-slate-700 z-10">
            <X className="w-6 h-6" />
          </button>
          <img src={lightboxImg} alt="Preview" className="max-w-full max-h-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl"><Bot className="w-6 h-6 text-white" /></div>
        <div>
          <h2 className="text-xl font-bold text-white" data-testid="mass-upload-title">Trabajador Virtual</h2>
          <p className="text-slate-400 text-sm">Carga masiva: procesa uno a uno para revisar y aprobar</p>
        </div>
        {totalVersions > 0 && (
          <div className="ml-auto flex items-center gap-2 text-xs">
            {completedCount > 0 && <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full">{completedCount} guardados</span>}
            {pendingCount > 0 && <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full">{pendingCount} pendientes</span>}
            <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded-full">{totalVersions} total</span>
          </div>
        )}
      </div>

      {/* ==================== PASO 1: Excel ==================== */}
      <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700" data-testid="step-1-excel">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold text-xs">1</div>
          <h3 className="text-base font-semibold text-white">Lista de Precios (Excel)</h3>
          <span className="text-slate-500 text-xs">— Opcional</span>
          {priceListData && <span className="ml-auto px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-xs">{priceListData.length} vehículos</span>}
        </div>
        <label className="flex items-center justify-center gap-3 p-4 border-2 border-dashed border-slate-600 rounded-lg hover:border-blue-500 cursor-pointer transition-colors" data-testid="excel-upload-label">
          {loadingPrices ? <Loader2 className="w-5 h-5 text-blue-400 animate-spin" /> : <FileSpreadsheet className="w-6 h-6 text-slate-400" />}
          <div>
            <p className="text-white text-sm font-medium">{priceListFile ? priceListFile.name : "Seleccionar archivo Excel"}</p>
            <p className="text-slate-400 text-xs">CABECERA, DESCRIPCION, PRECIO LISTA, PRECIO AAA</p>
          </div>
          <input type="file" accept=".xlsx,.xls" onChange={handlePriceListUpload} className="hidden" data-testid="excel-file-input" />
        </label>
        {priceListData && priceListData.length > 0 && (
          <div className="mt-3 max-h-32 overflow-y-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-slate-400 border-b border-slate-700">
                <th className="text-left py-1 px-2">Cód</th><th className="text-left py-1 px-2">Modelo</th><th className="text-left py-1 px-2">Versión</th>
                <th className="text-center py-1 px-2">Año</th><th className="text-right py-1 px-2">Lista</th><th className="text-right py-1 px-2">AAA</th>
              </tr></thead>
              <tbody>
                {priceListData.map((v, i) => (
                  <tr key={i} className="text-slate-300 border-b border-slate-800">
                    <td className="py-1 px-2 text-slate-500">{v.code}</td>
                    <td className="py-1 px-2 font-medium">{v.model}</td>
                    <td className="py-1 px-2 text-slate-400">{v.version}</td>
                    <td className="py-1 px-2 text-center text-blue-400 font-medium">{v.year || "—"}</td>
                    <td className="py-1 px-2 text-right text-green-400">${parseInt(v.price_list || 0).toLocaleString()}</td>
                    <td className="py-1 px-2 text-right text-amber-400">${parseInt(v.price_aaa || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ==================== PASO 2: PDFs ==================== */}
      <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700" data-testid="step-2-pdfs">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-full bg-orange-500/20 flex items-center justify-center text-orange-400 font-bold text-xs">2</div>
          <h3 className="text-base font-semibold text-white">Fichas Técnicas (PDFs)</h3>
          {specSheetFiles.length > 0 && <span className="ml-auto px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full text-xs">{specSheetFiles.length} archivos</span>}
        </div>
        <label className="flex items-center justify-center gap-3 p-4 border-2 border-dashed border-slate-600 rounded-lg hover:border-orange-500 cursor-pointer transition-colors mb-2" data-testid="pdf-upload-label">
          <FileText className="w-6 h-6 text-slate-400" />
          <div>
            <p className="text-white text-sm font-medium">Seleccionar fichas técnicas</p>
            <p className="text-slate-400 text-xs">PDFs o imágenes — múltiples</p>
          </div>
          <input ref={specInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" multiple onChange={handleSpecSheetsUpload} className="hidden" data-testid="pdf-file-input" />
        </label>
        {specSheetFiles.length > 0 && (
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {specSheetFiles.map((file, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                  <span className="text-white text-xs truncate">{file.name}</span>
                </div>
                <button onClick={() => removeSpecSheet(i)} className="p-1 text-red-400 hover:bg-red-500/20 rounded flex-shrink-0" data-testid={`remove-spec-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ==================== PASO 3: Extraer + Procesar ==================== */}
      <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700" data-testid="step-3-process">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 font-bold text-xs">3</div>
          <h3 className="text-base font-semibold text-white">Extraer y Procesar</h3>
        </div>

        {/* Extract button */}
        {versionQueue.length === 0 && (
          <button onClick={startExtraction} disabled={specSheetFiles.length === 0 || isExtracting}
            className="w-full py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-lg flex items-center justify-center gap-2 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            data-testid="start-extraction-btn">
            {isExtracting ? (<><Loader2 className="w-5 h-5 animate-spin" />Extrayendo datos de fichas...</>) : (<><List className="w-5 h-5" />Extraer Datos de Todas las Fichas</>)}
          </button>
        )}

        {/* Extraction progress */}
        {fileQueue.length > 0 && (
          <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
            {fileQueue.map((f) => (
              <div key={f.id} className="flex items-center justify-between p-2 bg-slate-900/50 rounded-lg text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  {f.status === "extracting" && <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin flex-shrink-0" />}
                  {f.status === "done" && <CheckCircle2 className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />}
                  {f.status === "error" && <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />}
                  {f.status === "pending" && <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                  <span className="text-white truncate">{f.fileName}</span>
                </div>
                {f.error && <span className="text-red-400 ml-2 flex-shrink-0">{f.error.slice(0, 30)}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Version list + process next */}
        {versionQueue.length > 0 && !isExtracting && (
          <div className="mt-4">
            <p className="text-slate-400 text-xs mb-2">{totalVersions} versiones detectadas:</p>
            <div className="space-y-1 max-h-40 overflow-y-auto mb-3">
              {versionQueue.map((v) => (
                <div key={v.id} className={`flex items-center justify-between p-2 rounded-lg text-xs ${
                  v.status === "completed" ? "bg-green-500/10" : v.status === "review" || v.status === "generating" ? "bg-yellow-500/10" : v.status === "discarded" ? "bg-slate-800 opacity-50" : "bg-slate-900/50"
                }`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {v.status === "completed" && <CheckCircle2 className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />}
                    {v.status === "generating" && <Loader2 className="w-3.5 h-3.5 text-orange-400 animate-spin flex-shrink-0" />}
                    {v.status === "review" && <Eye className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />}
                    {v.status === "pending_generation" && <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                    {v.status === "discarded" && <Trash2 className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />}
                    <span className="text-white truncate">{v.brand} {v.modelName} — {v.versionName}</span>
                    {v.year && <span className="text-blue-400 flex-shrink-0">{v.year}</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {v.editedPrice && <span className="text-green-400">${parseInt(v.editedPrice || 0).toLocaleString()}</span>}
                    {v.status === "pending_generation" && !isGenerating && (
                      <button
                        onClick={() => setCurrentVehicle({ ...v })}
                        className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 transition-colors"
                        title="Revisar specs + precio antes de generar"
                        data-testid={`review-btn-${v.id}`}
                      >
                        <ZoomIn className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {pendingCount > 0 && !isGenerating && !currentVehicle && (
              <button onClick={processNextVehicle}
                className="w-full py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-lg flex items-center justify-center gap-2 hover:from-blue-600 hover:to-cyan-600 transition-all"
                data-testid="process-next-btn">
                <Eye className="w-5 h-5" /> Revisar Siguiente ({pendingCount} pendientes)
              </button>
            )}
          </div>
        )}
      </div>

      {/* ==================== PASO 4: Revisión del vehículo actual ==================== */}
      {currentVehicle && (
        <div className="bg-slate-800/50 rounded-xl p-5 border-2 border-yellow-500/30" data-testid="step-4-review">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-400 font-bold text-xs">4</div>
            <h3 className="text-base font-semibold text-white">
              {isGenerating ? "Generando..." : currentVehicle.status === "completed" ? "Completado" : "Revisión"}
            </h3>
            <span className="ml-auto text-white text-sm font-medium">
              {currentVehicle.brand} {currentVehicle.modelName}
              {currentVehicle.year && <span className="text-blue-400 ml-1">{currentVehicle.year}</span>}
            </span>
          </div>

          {/* Generating progress */}
          {isGenerating && (
            <div className="flex items-center gap-3 p-4 bg-orange-500/10 rounded-lg mb-4">
              <Loader2 className="w-5 h-5 text-orange-400 animate-spin flex-shrink-0" />
              <span className="text-orange-300 text-sm">{generationStep || "Generando..."}</span>
            </div>
          )}

          {/* Vehicle info */}
          {!isGenerating && (
            <div className="space-y-4">
              {/* Info grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <div><span className="text-slate-400 text-xs">Marca:</span> <span className="text-white">{currentVehicle.brand}</span></div>
                <div><span className="text-slate-400 text-xs">Modelo:</span> <span className="text-white">{currentVehicle.modelName}</span></div>
                <div><span className="text-slate-400 text-xs">Versión:</span> <span className="text-white">{currentVehicle.versionName}</span></div>
                <div><span className="text-slate-400 text-xs">Año:</span> <span className="text-blue-400 font-bold">{currentVehicle.year || "N/A"}</span></div>
              </div>

              {/* Prices */}
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-slate-400 text-xs mb-2 flex items-center gap-1">
                  <DollarSign className="w-3 h-3" /> Precios {currentVehicle.matchedPrice ? "(del Excel)" : "(manuales)"}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-slate-500 text-xs">Precio Lista</label>
                    <input type="number" className="w-full bg-slate-900 text-white rounded px-3 py-1.5 text-sm border border-slate-700 focus:border-green-500 focus:outline-none"
                      value={currentVehicle.editedPrice || ""}
                      onChange={(e) => {
                        setCurrentVehicle((prev) => ({ ...prev, editedPrice: e.target.value }));
                        setVersionQueue((prev) => prev.map((v) => v.id === currentVehicle.id ? { ...v, editedPrice: e.target.value } : v));
                      }}
                      placeholder="0" data-testid="current-price-input" />
                  </div>
                  <div>
                    <label className="text-slate-500 text-xs">Precio AAA</label>
                    <input type="number" className="w-full bg-slate-900 text-white rounded px-3 py-1.5 text-sm border border-slate-700 focus:border-amber-500 focus:outline-none"
                      value={currentVehicle.editedPurchase || ""}
                      onChange={(e) => {
                        setCurrentVehicle((prev) => ({ ...prev, editedPurchase: e.target.value }));
                        setVersionQueue((prev) => prev.map((v) => v.id === currentVehicle.id ? { ...v, editedPurchase: e.target.value } : v));
                      }}
                      placeholder="0" data-testid="current-purchase-input" />
                  </div>
                </div>
              </div>

              {/* Specs */}
              {currentVehicle.specifications?.length > 0 && (
                <div>
                  <p className="text-slate-400 text-xs mb-1">Especificaciones:</p>
                  <div className="flex flex-wrap gap-1">
                    {currentVehicle.specifications.slice(0, 9).map((s, i) => (
                      <span key={i} className="text-xs bg-slate-800 text-slate-300 px-2 py-0.5 rounded">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Paso de revisión antes de generar: mostrar botón grande "Iniciar generación"
                  cuando el vehículo está en pending_generation. Así el usuario verifica specs
                  y precio ANTES de que empiecen a generarse las imágenes. */}
              {currentVehicle.status === "pending_generation" && !isGenerating && (
                <div className="bg-blue-500/10 border border-blue-500/40 rounded-lg p-4 space-y-3">
                  <p className="text-blue-300 text-sm font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Revisa las especificaciones y el precio arriba antes de generar
                  </p>
                  <p className="text-slate-400 text-xs">
                    Una vez que confirmes, se generarán 8 imágenes (Hook, Lifestyle, Trabajo, Ficha, Web1-3, Stories) y los copies para redes. Esto tarda ~1-2 minutos.
                  </p>
                  <button
                    onClick={() => processVehicle(currentVehicle.id)}
                    className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-lg flex items-center justify-center gap-2 hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg"
                    data-testid="start-generation-btn"
                  >
                    <Zap className="w-5 h-5" />
                    Iniciar Generación de Imágenes
                  </button>
                </div>
              )}

              {/* WEB IMAGES */}
              {currentVehicle.webImages?.length > 0 && (
                <div>
                  <p className="text-slate-400 text-xs mb-2 flex items-center gap-1"><Globe className="w-3 h-3" /> Imágenes Web (catálogo) — click para ver grande</p>
                  <div className="grid grid-cols-3 gap-2">
                    {currentVehicle.webImages.map((img, i) => <ImageThumb key={i} img={img} imgCategory="web" imgIndex={i} large />)}
                  </div>
                </div>
              )}

              {/* SOCIAL IMAGES */}
              {currentVehicle.socialImages?.length > 0 && (
                <div>
                  <p className="text-slate-400 text-xs mb-2 flex items-center gap-1"><Instagram className="w-3 h-3" /> Imágenes Redes Sociales</p>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {currentVehicle.socialImages.map((img, i) => <ImageThumb key={i} img={img} imgCategory="social" imgIndex={i} />)}
                  </div>
                </div>
              )}

              {/* Copy preview EDITABLE (Fix: usuario puede modificar el copy antes de aprobar) */}
              {(currentVehicle.copyFeed || currentVehicle.copyStories) && (
                <div className="space-y-3">
                  <div className="bg-slate-800 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-slate-400 text-xs font-medium">Copy Feed (editable):</p>
                      <button
                        onClick={() => regenerateCopy("feed")}
                        disabled={currentVehicle.copyRegenerating === "feed"}
                        className="flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300 disabled:opacity-50"
                        data-testid="regen-copy-feed-btn"
                      >
                        <RefreshCw className={`w-3 h-3 ${currentVehicle.copyRegenerating === "feed" ? "animate-spin" : ""}`} />
                        Regenerar copy
                      </button>
                    </div>
                    <textarea
                      value={currentVehicle.copyFeed || ""}
                      onChange={(e) => {
                        const upd = { ...currentVehicle, copyFeed: e.target.value };
                        setCurrentVehicle(upd);
                        setVersionQueue((prev) => prev.map((v) => v.id === currentVehicle.id ? upd : v));
                      }}
                      rows={4}
                      className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-slate-200 text-xs focus:border-orange-500 focus:outline-none resize-y"
                      data-testid="copy-feed-textarea"
                    />
                    {currentVehicle.hashtagsFeed?.length > 0 && (
                      <p className="text-blue-400 text-xs mt-1">{currentVehicle.hashtagsFeed.join(" ")}</p>
                    )}
                  </div>
                  {currentVehicle.copyStories && (
                    <div className="bg-slate-800 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-slate-400 text-xs font-medium">Copy Stories (editable):</p>
                        <button
                          onClick={() => regenerateCopy("stories")}
                          disabled={currentVehicle.copyRegenerating === "stories"}
                          className="flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300 disabled:opacity-50"
                          data-testid="regen-copy-stories-btn"
                        >
                          <RefreshCw className={`w-3 h-3 ${currentVehicle.copyRegenerating === "stories" ? "animate-spin" : ""}`} />
                          Regenerar copy
                        </button>
                      </div>
                      <textarea
                        value={currentVehicle.copyStories || ""}
                        onChange={(e) => {
                          const upd = { ...currentVehicle, copyStories: e.target.value };
                          setCurrentVehicle(upd);
                          setVersionQueue((prev) => prev.map((v) => v.id === currentVehicle.id ? upd : v));
                        }}
                        rows={3}
                        className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-slate-200 text-xs focus:border-orange-500 focus:outline-none resize-y"
                        data-testid="copy-stories-textarea"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Action buttons */}
              {currentVehicle.status === "review" && (
                <div className="flex gap-2">
                  <button onClick={saveCurrentVehicle} disabled={isSaving}
                    className="flex-1 py-3 bg-green-500 text-white rounded-lg flex items-center justify-center gap-2 hover:bg-green-600 disabled:opacity-50 transition-colors font-semibold"
                    data-testid="approve-save-btn">
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Aprobar y Guardar
                  </button>
                  <button onClick={discardCurrentVehicle}
                    className="py-3 px-4 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                    data-testid="discard-btn">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}

              {currentVehicle.status === "completed" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 py-2 px-4 bg-green-500/10 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 text-sm">Guardado en BD + Publicaciones</span>
                  </div>
                  {pendingCount > 0 && (
                    <button onClick={goToNextVehicle}
                      className="w-full py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-lg flex items-center justify-center gap-2 hover:from-blue-600 hover:to-cyan-600 transition-all"
                      data-testid="next-vehicle-btn">
                      <SkipForward className="w-5 h-5" /> Revisar Siguiente ({pendingCount} pendientes)
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Completed summary */}
      {completedVehicles.length > 0 && !currentVehicle && (
        <div className="bg-green-500/5 rounded-xl p-4 border border-green-500/20">
          <p className="text-green-400 text-sm font-medium mb-2 flex items-center gap-1">
            <CheckCircle2 className="w-4 h-4" /> {completedVehicles.length} vehículos guardados
          </p>
          {completedVehicles.map((v, i) => (
            <p key={i} className="text-green-300 text-xs ml-5">{v.brand} {v.modelName} {v.versionName} {v.year} — ${parseInt(v.editedPrice || 0).toLocaleString()}</p>
          ))}
        </div>
      )}

      {/* Error list */}
      {fileQueue.some((f) => f.status === "error") && (
        <div className="bg-red-500/5 rounded-xl p-4 border border-red-500/20">
          <p className="text-red-400 text-sm font-medium mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Errores:</p>
          {fileQueue.filter((f) => f.status === "error").map((f) => (
            <p key={f.id} className="text-red-300 text-xs ml-5">{f.fileName}: {f.error}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default MassUploadTab;
